/* @flow */

import * as React from 'react';

import {Popover} from '../popover';
import {ScrollableContainerWithShadow, Stack} from '../layout';
import {LoadingPage} from '../loading-page';

import type {Item} from './types';

import {ListItem} from './list-item';

import {getUpdatedItemsList} from './helpers';

import './mini-list-popover.css';

export type Props = {|
    anchor: any,
    collection: Item[],
    renderRow: (item: Item) => React.Node,
    // For controlled (by parent) input - do not pass both, pass none for readonly version
    onSelect?: (id: string) => void,
    // For uncontrolled (by parent) input - do not pass both, pass none for readonly version
    onBlur?: (updatedItems: Item[]) => void,
    isSaving?: boolean,
|};

export function MiniListPopover(props: Props) {
    const {collection, anchor, renderRow, onSelect, onBlur, isSaving} = props;

    // Internal state for uncontrolled (by parent) input, if the list is selectable
    const [selectableItemsList, setSelectableItemsList] = React.useState<?(Item[])>(undefined);

    React.useEffect(() => {
        if (onBlur && !selectableItemsList) {
            setSelectableItemsList(collection);
        }
    }, [collection, selectableItemsList, onBlur]);

    let handleOnSelect;
    if (onSelect) {
        handleOnSelect = (id: string) => onSelect(id);
    } else if (selectableItemsList) {
        handleOnSelect = (id: string) => {
            setSelectableItemsList((prevState) =>
                prevState ? getUpdatedItemsList(prevState, id) : undefined
            );
        };
    }

    const shouldListScroll = collection.length > 5;
    const Container = shouldListScroll ? ScrollableContainerWithShadow : 'div';
    const scrollProps = shouldListScroll
        ? {
              direction: 'vertical',
              maxContainerSize: 220,
              showReverseGradientShadow: true,
          }
        : {};

    return anchor && anchor.current ? (
        <Popover
            anchor={anchor.current}
            location='bottom-left'
            onBlur={
                onBlur
                    ? () => onBlur(selectableItemsList ? selectableItemsList : collection)
                    : undefined
            }
        >
            <div styleName='mini-list-popover'>
                {isSaving ? (
                    <div styleName='loading-container'>
                        <LoadingPage padding={0} />
                    </div>
                ) : (
                    undefined
                )}
                <Container {...scrollProps}>
                    <Stack spacing={4}>
                        {(selectableItemsList || collection).map((item) => (
                            <ListItem
                                key={item.id}
                                id={item.id}
                                content={renderRow(item)}
                                isChecked={item.isChecked}
                                onSelect={handleOnSelect}
                                isDisabled={isSaving}
                            />
                        ))}
                    </Stack>
                </Container>
            </div>
        </Popover>
    ) : null;
}
